import type { ReactNode } from 'react';
import type { AdvisorProductType, AdvisorRegion } from '../model/types';
import { X } from 'lucide-react';
import { joinCss } from '@/shared/lib/css-utils';
import { Select } from '@/shared/ui/select';
import { Tab, TabBar } from '@/shared/ui/tab-bar';
import { Text } from '@/shared/ui/text';
import {
  displayRegionTitle,
  titleCaseRegionTitle,
} from '../model/region-layout';
import { advisorProductTypes } from '../model/types';
import css from './advisor-planner-workspace.module.scss';

const TYPE_LABELS: Record<AdvisorProductType, string> = {
  hotels: 'Hotels',
  experiences: 'Experiences',
  restaurants: 'Restaurants',
};

type FilterBarProps = {
  country: string;
  countries: string[];
  regions: AdvisorRegion[];
  moods: string[];
  tags: string[];
  selectedRegionIds: string[];
  activeRegionId: string | null;
  moodRegionIds: string[];
  selectedMoods: string[];
  selectedTags: string[];
  selectedType: AdvisorProductType;
  onSelectCountry: (country: string) => void;
  onSelectRegion: (regionId: string) => void;
  onRemoveRegion: (regionId: string) => void;
  onSelectMood: (mood: string) => void;
  onSelectTag: (tag: string) => void;
  onSelectType: (type: AdvisorProductType) => void;
};

export const FilterBar = ({
  country,
  countries,
  regions,
  moods,
  tags,
  selectedRegionIds,
  activeRegionId,
  moodRegionIds,
  selectedMoods,
  selectedTags,
  selectedType,
  onSelectCountry,
  onSelectRegion,
  onRemoveRegion,
  onSelectMood,
  onSelectTag,
  onSelectType,
}: FilterBarProps) => {
  const countryOptions = (countries.length > 0 ? countries : [country]).map(
    (entry) => ({ value: entry, label: entry })
  );
  return (
    <div className={css.filters}>
      <FilterRow label="Country">
        <Select
          aria-label="Country"
          className={css.countrySelect}
          dropdownMaxHeight={240}
          onChange={(event) => onSelectCountry(event.target.value)}
          options={countryOptions}
          searchable
          searchPlaceholder="Country"
          size="sm"
          usePortal
          value={country}
        />
      </FilterRow>
      <FilterRow label="Region">
        {regions.map((region) => {
          const listed = selectedRegionIds.includes(region.id);
          const active = region.id === activeRegionId;
          const moodHit = moodRegionIds.includes(region.id);
          const title = displayRegionTitle(region.title);
          return (
            <span className={css.regionChip} key={region.id}>
              <Tab
                aria-current={active ? 'true' : undefined}
                aria-pressed={listed}
                className={
                  active
                    ? css.chipActive
                    : listed
                      ? css.chipListed
                      : moodHit
                        ? css.chipMood
                        : undefined
                }
                onClick={() => onSelectRegion(region.id)}
                size="sm"
                type="button"
              >
                {title}
              </Tab>
              {listed ? (
                <button
                  aria-label={`Remove ${title}`}
                  className={joinCss(
                    css.regionChipRemove,
                    active && css.regionChipRemoveOnActive
                  )}
                  onClick={() => onRemoveRegion(region.id)}
                  type="button"
                >
                  <X size={12} />
                </button>
              ) : null}
            </span>
          );
        })}
      </FilterRow>
      <FilterRow label="Mood">
        {moods.map((mood) => {
          const selected = selectedMoods.includes(mood);
          return (
            <Tab
              key={mood}
              aria-pressed={selected}
              className={selected ? css.chipActive : undefined}
              onClick={() => onSelectMood(mood)}
              size="sm"
              type="button"
            >
              {titleCaseRegionTitle(mood)}
            </Tab>
          );
        })}
      </FilterRow>
      <FilterRow label="Type">
        <TabBar>
          {advisorProductTypes.map((type) => (
            <Tab
              key={type}
              aria-pressed={selectedType === type}
              className={selectedType === type ? css.chipActive : undefined}
              onClick={() => onSelectType(type)}
              size="sm"
              type="button"
            >
              {TYPE_LABELS[type]}
            </Tab>
          ))}
        </TabBar>
      </FilterRow>
      {tags.length > 0 && (
        <FilterRow label="Tags">
          {tags.map((tag) => {
            const selected = selectedTags.includes(tag);
            return (
              <Tab
                key={tag}
                aria-pressed={selected}
                className={selected ? css.chipActive : undefined}
                onClick={() => onSelectTag(tag)}
                size="sm"
                type="button"
              >
                {titleCaseRegionTitle(tag)}
              </Tab>
            );
          })}
        </FilterRow>
      )}
    </div>
  );
};

const FilterRow = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <div className={css.filterRow}>
    <Text className={css.filterLabel} size="sm" weight="medium">
      {label}
    </Text>
    <div className={css.filterPills}>{children}</div>
  </div>
);
